import { useCallback, useEffect, useReducer } from 'react';
import { logger } from 'packages/logger';

function fetchReducer(state, action) {
    switch (action.type) {
        case 'error': {
            return {
                ...state,
                status: 'rejected',
                error: action.error
            };
        }
        case 'success': {
            return {
                ...state,
                status: 'resolved',
                data: action.data
            };
        }
        case 'started': {
            return {
                ...state,
                status: 'fetching'
            };
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
}

/**
 * Fetch data hook
 * @param  {Function?} apiFunction Function which makes the async API request
 * @param  {String} apiParameter (Optional) Variable that could be passed to apiFunction
 * @param  {String} apiName (Optional) The name of the API request for logging purposes
 * @return {uknown} Object containing the data, status and retry function for the request.
 */

export function useFetchData(apiFunction, apiParameter, apiName) {
    const [state, dispatch] = useReducer(fetchReducer, {
        status: apiFunction ? 'idle' : 'resolved',
        data: {},
        error: null
    });

    const handleFetch = useCallback(async () => {
        dispatch({ type: 'started' });
        apiFunction(apiParameter)
            .then(({ data }) => {
                dispatch({ type: 'success', data });
                return;
            })
            .catch((error) => {
                dispatch({ type: 'error', error });
                logger(
                    new Error(
                        `Fetch ${apiName || apiParameter} failed ${error}`
                    )
                );
            });
    }, [apiFunction, apiParameter, apiName]);

    useEffect(() => {
        if (apiFunction) {
            handleFetch();
        }
    }, [handleFetch, apiFunction]);

    const retryFetch = () => {
        handleFetch();
    };

    return {
        isLoading: state.status === 'idle' || state.status === 'fetching',
        isIdle: state.status === 'idle',
        isFetching: state.status === 'fetching',
        isResolved: state.status === 'resolved',
        isRejected: state.status === 'rejected',
        retryFetch,
        ...state
    };
}
